import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePaymentStatus } from '../hooks/usePaymentStatus';
import './CheckoutSuccess.css';

interface CheckoutSuccessProps {}

interface PurchaseDetails {
  sessionId: string;
  tickets: Array<{
    id: string;
    holderName: string;
    holderEmail: string;
    category: string;
    eventTitle: string;
    eventDate: string;
    eventLocation: string;
    qrCode?: string;
    price: number;
    currency: string;
  }>;
  totalAmount: number;
  currency: string;
  purchaseDate: string;
  eventDetails: {
    title: string;
    startDateTime: string;
    location: string;
    description: string;
  };
}

const CheckoutSuccess: React.FC<CheckoutSuccessProps> = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Get sessionId from URL params or query parameters
  const actualSessionId = sessionId || searchParams.get('sessionId');
  
  // Use our custom hook to check payment status
  const { loading, success, error, paymentData } = usePaymentStatus(actualSessionId || undefined);
  
  const [purchaseDetails, setPurchaseDetails] = useState<PurchaseDetails | null>(null);

  useEffect(() => {
    if (!loading && success && paymentData) {
      // Convert API response to our UI format
      const details: PurchaseDetails = {
        sessionId: actualSessionId || 'unknown',
        tickets: [
          {
            id: `ticket-${paymentData.payment?.id || 'unknown'}`,
            holderName: paymentData.payment?.metadata?.userName || 'Unknown',
            holderEmail: paymentData.payment?.metadata?.userEmail || 'unknown@example.com',
            category: 'General',
            eventTitle: paymentData.payment?.metadata?.eventTitle || 'Event',
            eventDate: new Date().toISOString(),
            eventLocation: 'Location TBD',
            price: (paymentData.payment?.amount || 0) / 100,
            currency: (paymentData.payment?.currency || 'EUR').toUpperCase()
          }
        ],
        totalAmount: (paymentData.payment?.amount || 0) / 100,
        currency: (paymentData.payment?.currency || 'EUR').toUpperCase(),
        purchaseDate: paymentData.payment?.createdAt || new Date().toISOString(),
        eventDetails: {
          title: paymentData.payment?.metadata?.eventTitle || 'Event',
          startDateTime: new Date().toISOString(),
          location: 'Location TBD',
          description: 'Event description'
        }
      };
      
      setPurchaseDetails(details);
    }
  }, [loading, success, paymentData, actualSessionId]);

  const handleBackToEvents = () => {
    navigate('/events');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  if (loading) {
    return (
      <div className="checkout-success loading">
        <div className="loading-spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  if (error || (!success && !loading) || !purchaseDetails) {
    return (
      <div className="checkout-success error">
        <div className="error-container">
          <div className="error-icon">❌</div>
          <h2>{t('checkout.error')}</h2>
          <p>{error || t('checkout.errorLoadingDetails')}</p>
          <button onClick={handleBackToEvents} className="btn-primary">
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-success">
      <div className="success-container">
        {/* Success Header */}
        <div className="success-header">
          <div className="success-icon">✅</div>
          <h1>{t('checkout.purchaseSuccessful')}</h1>
          <p className="success-message">{t('checkout.purchaseSuccessMessage')}</p>
        </div>

        {/* Event Details */}
        <div className="event-summary">
          <h3>{purchaseDetails.eventDetails.title}</h3>
          <div className="event-meta">
            <div className="meta-item">
              <span className="icon">📅</span>
              <span>{formatDate(purchaseDetails.eventDetails.startDateTime)}</span>
            </div>
            <div className="meta-item">
              <span className="icon">📍</span>
              <span>{purchaseDetails.eventDetails.location}</span>
            </div>
          </div>
        </div>

        {/* Purchase Summary */}
        <div className="purchase-summary">
          <div className="summary-header">
            <h3>{t('checkout.purchaseSummary')}</h3>
            <div className="session-id">
              <small>{t('checkout.sessionId')}: {purchaseDetails.sessionId}</small>
            </div>
          </div>
          
          <div className="purchase-details">
            <div className="detail-row">
              <span>{t('checkout.purchaseDate')}</span>
              <span>{formatDate(purchaseDetails.purchaseDate)}</span>
            </div>
            <div className="detail-row total">
              <span>{t('checkout.totalPaid')}</span>
              <span className="amount">{formatPrice(purchaseDetails.totalAmount, purchaseDetails.currency)}</span>
            </div>
          </div>
        </div>

        {/* Tickets List */}
        <div className="tickets-list">
          <h3>{t('checkout.yourTickets')} ({purchaseDetails.tickets.length})</h3>
          
          {purchaseDetails.tickets.map((ticket, index) => (
            <div key={ticket.id} className="ticket-card">
              <div className="ticket-header">
                <div className="ticket-number">
                  {t('checkout.ticket')} #{index + 1}
                </div>
                <div className="ticket-price">
                  {formatPrice(ticket.price, ticket.currency)}
                </div>
              </div>
              
              <div className="ticket-details">
                <div className="detail-row">
                  <span className="label">{t('checkout.holderName')}:</span>
                  <span className="value">{ticket.holderName}</span>
                </div>
                <div className="detail-row">
                  <span className="label">{t('checkout.holderEmail')}:</span>
                  <span className="value">{ticket.holderEmail}</span>
                </div>
                <div className="detail-row">
                  <span className="label">{t('checkout.category')}:</span>
                  <span className="value">{ticket.category}</span>
                </div>
                <div className="detail-row">
                  <span className="label">{t('checkout.ticketId')}:</span>
                  <span className="value ticket-id">{ticket.id}</span>
                </div>
              </div>
              
              {ticket.qrCode && (
                <div className="qr-code">
                  <img src={ticket.qrCode} alt={`QR Code ${ticket.id}`} />
                  <p>{t('checkout.qrCodeInstructions')}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Next Steps */}
        <div className="next-steps">
          <h3>{t('checkout.nextSteps')}</h3>
          <div className="steps-list">
            <div className="step-item">
              <span className="step-icon">📧</span>
              <div className="step-content">
                <h4>{t('checkout.checkEmail')}</h4>
                <p>{t('checkout.checkEmailDescription')}</p>
              </div>
            </div>
            <div className="step-item">
              <span className="step-icon">📱</span>
              <div className="step-content">
                <h4>{t('checkout.saveTickets')}</h4>
                <p>{t('checkout.saveTicketsDescription')}</p>
              </div>
            </div>
            <div className="step-item">
              <span className="step-icon">🎫</span>
              <div className="step-content">
                <h4>{t('checkout.eventDay')}</h4>
                <p>{t('checkout.eventDayDescription')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="success-actions">
          <button onClick={handleBackToEvents} className="btn-primary">
            {t('checkout.backToEvents')}
          </button>
          <button onClick={() => window.print()} className="btn-secondary">
            {t('checkout.printTickets')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess;